type Handler<P> = (payload: P) => void;

/**
 * Minimal strongly-typed event emitter. Subclasses declare their event map and
 * `emit` internally; consumers use the public `on` / `once` / `off`.
 *
 *   class Foo extends EventEmitter<{ ready: { id: string } }> {
 *     done() { this.emit('ready', { id: 'x' }); }
 *   }
 */
export class EventEmitter<T extends Record<string, unknown>> {
  // Storage is type-erased (Handler<never>); the public API restores type-safety.
  private readonly listeners = new Map<keyof T, Set<Handler<never>>>();

  on<K extends keyof T>(type: K, handler: Handler<T[K]>): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(handler as Handler<never>);
    return () => this.off(type, handler);
  }

  once<K extends keyof T>(type: K, handler: Handler<T[K]>): () => void {
    const wrapped: Handler<T[K]> = (payload) => {
      this.off(type, wrapped);
      handler(payload);
    };
    return this.on(type, wrapped);
  }

  off<K extends keyof T>(type: K, handler: Handler<T[K]>): void {
    this.listeners.get(type)?.delete(handler as Handler<never>);
  }

  protected emit<K extends keyof T>(type: K, payload: T[K]): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const handler of set) (handler as Handler<T[K]>)(payload);
  }

  protected clearListeners(): void {
    this.listeners.clear();
  }
}
